<template>
  <main>
    <div v-if="currentUser" class="max-w-7xl mx-auto sm:px-6 lg:px-8 py-9">
      <div class="grid grid-cols-4 gap-4">
        <div class="col-span-1">
          <litepie-datepicker
            ref="litePieDatePicker"
            :auto-apply="false"
            :formatter="formatter"
            v-model="dateRange"
          />
        </div>
        <div class="col-span-3">
          <div v-if="currentUser.user_metadata.timezone" class="text-gray-400 mt-2 text-sm">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 inline-block" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
            </svg>
            {{ currentTimezone }}
          </div>
          <div v-else class="text-yellow-500 mt-2 text-sm">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 inline-block" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            No timezone set in your profile settings. Using {{ currentTimezone }}.
          </div>

          <div class="mt-3 text-sm text-gray-500">
            Reports are now generated from real-time tracking events.
          </div>
        </div>
      </div>

      <template v-if="loadingData">
        <Loading />
      </template>
      <template v-else>
        <div v-if="habitHeatmaps.length" class="pt-5">
          <h2 class="text-2xl mb-3">Habits</h2>
          <div class="grid grid-cols-1 gap-4">
            <HabitHeatmap
              v-for="h in habitHeatmaps"
              :key="h.blockId"
              :title="h.title"
              :color="h.color"
              :from="habitFrom"
              :to="habitTo"
              :tz="currentTimezone"
              :totalsByDate="h.totalsByDate"
              :goal="h.goal"
              :totalAll="h.totalAll"
            />
          </div>
        </div>

        <div class="grid grid-cols-4 pt-5">
          <div class="col-span-1">
            <h2 class="text-2xl">Total time</h2>
            <span class="text-xl">{{ totalTime }}</span>
          </div>
          <div class="col-span-1">
            <DonutChart
              :values="reportsProjectsDonutChart.values"
              :colors="reportsProjectsDonutChart.colors"
              :labels="reportsProjectsDonutChart.labels"
            />
          </div>
          <div class="col-span-1">
            <DonutChart
              :values="reportsTitlesDonutChart.values"
              :colors="reportsTitlesDonutChart.colors"
              :labels="reportsTitlesDonutChart.labels"
            />
          </div>
          <div class="col-span-1">
            <DonutChart
              :values="reportsColorsDonutChart.values"
              :colors="reportsColorsDonutChart.colors"
              :labels="reportsColorsDonutChart.labels"
            />
          </div>
        </div>

        <div id="chart">
          <BarChart :values="reportsProjectsBarChart" :categories="reportsDateRange" />
        </div>

        <div id="chart3">
          <BarChart :values="reportsTitlesBarChart" :categories="reportsDateRange" />
        </div>

        <div id="chart2">
          <BarChart :values="reportsColorsBarChart" :categories="reportsDateRange" />
        </div>

        <div id="timeChart">
          <RangeBarChart :values="reportsProjectsRangeBarChart" />
        </div>

        <div id="timeChart3">
          <RangeBarChart :values="reportsTitlesRangeBarChart" />
        </div>

        <div id="timeChart2">
          <RangeBarChart :values="reportsColorsRangeBarChart" />
        </div>
      </template>
    </div>
  </main>
</template>

<script>
  import { mapGetters } from "vuex";
  import { ref } from 'vue';
  import LitepieDatepicker from 'litepie-datepicker';
  import Loading from '../components/Loading';
  import { getTrackingRange } from "../models/TrackingElementsModel";
  import { getBlocks } from "../models/BlocksModel";
  import dayjs from 'dayjs';
  import utc from "dayjs/plugin/utc";
  import timezone from "dayjs/plugin/timezone";
  import customParseFormat from 'dayjs/plugin/customParseFormat';

  import {
    reportsTotalTime,
    reportsPrepareDates,
    reportsBarChart,
    reportsRangeBarChart,
    reportsDonutChart
  } from "../reports/reports";
  import { normalizeTrackingElements } from "../reports/trackingReports";
  import { buildCounterHeatmaps } from "../reports/trackingHabits";

  import HabitHeatmap from "../reports/components/HabitHeatmap";

  import DonutChart from '../reports/components/DonutChart';
  import RangeBarChart from '../reports/components/RangeBarChart';
  import BarChart from '../reports/components/BarChart';

  dayjs.extend(utc);
  dayjs.extend(timezone);
  dayjs.extend(customParseFormat);

  export default {
    components: {
      LitepieDatepicker,
      Loading,
      DonutChart,
      RangeBarChart,
      BarChart,
      HabitHeatmap,
    },
    setup() {
      const dateRange = ref([
        dayjs().subtract(6, 'day').format('DD MMM YYYY'),
        dayjs().format('DD MMM YYYY'),
      ]);
      const formatter = ref({
        date: 'DD MMM YYYY',
        month: 'MMM'
      });

      const loadingData = ref(true);
      const reportsDateRange = ref([]);
      const totalTime = ref('00h 00m');

      const reportsProjectsBarChart = ref([]);
      const reportsColorsBarChart = ref([]);
      const reportsTitlesBarChart = ref([]);

      const reportsProjectsRangeBarChart = ref([]);
      const reportsColorsRangeBarChart = ref([]);
      const reportsTitlesRangeBarChart = ref([]);

      const reportsProjectsDonutChart = ref({ values: [], colors: [], labels: [] });
      const reportsColorsDonutChart = ref({ values: [], colors: [], labels: [] });
      const reportsTitlesDonutChart = ref({ values: [], colors: [], labels: [] });

      const currentTimezone = ref(dayjs.tz.guess());

      const habitHeatmaps = ref([]);
      const habitFrom = ref(0);
      const habitTo = ref(0);

      const counterGoalsByBlockId = ref({});

      return {
        dateRange,
        formatter,
        loadingData,
        reportsDateRange,
        totalTime,
        reportsProjectsBarChart,
        reportsColorsBarChart,
        reportsTitlesBarChart,
        reportsProjectsRangeBarChart,
        reportsColorsRangeBarChart,
        reportsTitlesRangeBarChart,
        reportsProjectsDonutChart,
        reportsColorsDonutChart,
        reportsTitlesDonutChart,
        currentTimezone,
        habitHeatmaps,
        habitFrom,
        habitTo,
        counterGoalsByBlockId,
      };
    },
    computed: {
      ...mapGetters("auth", ["currentUser"]),
    },
    methods: {
      async refreshReports() {
        if (!this.currentUser) return;

        this.loadingData = true;

        if (this.currentUser.user_metadata?.timezone) {
          this.currentTimezone = this.currentUser.user_metadata.timezone;
        }

        const fmt = (this.formatter && this.formatter.date) ? this.formatter.date : 'DD MMM YYYY';
        const reportTimezone = this.currentTimezone || dayjs.tz.guess();

        let from;
        let to;

        const hasTwo = Array.isArray(this.dateRange) && this.dateRange.length === 2;
        const startRaw = hasTwo ? this.dateRange[0] : null;
        const endRaw = hasTwo ? this.dateRange[1] : null;

        const start = startRaw ? dayjs.tz(startRaw, fmt, reportTimezone) : null;
        const end = endRaw ? dayjs.tz(endRaw, fmt, reportTimezone) : null;

        if (start && end && start.isValid() && end.isValid()) {
          from = start.startOf('day').valueOf();
          to = end.endOf('day').valueOf();
        } else {
          // Default to last 7 days
          from = dayjs().tz(reportTimezone).subtract(6, 'day').startOf('day').valueOf();
          to = dayjs().tz(reportTimezone).endOf('day').valueOf();
          this.dateRange = [dayjs(from).tz(reportTimezone).format(fmt), dayjs(to).tz(reportTimezone).format(fmt)];
        }

        const resp = await getTrackingRange(from, to);
        this.habitFrom = from;
        this.habitTo = to;

        // Fetch BlockCounter goals from blocks table (fallback for older tracking rows missing meta.size)
        if (!this.counterGoalsByBlockId || Object.keys(this.counterGoalsByBlockId).length === 0) {
          try {
            const blocksResp = await getBlocks();
            const goalMap = {};

            (blocksResp.data || []).forEach((b) => {
              if (!b || !b.id) return;
              const type = b.type || (b.data && b.data.type);
              if (type !== 'BlockCounter') return;
              const size = b.data && typeof b.data.size !== 'undefined' ? Number(b.data.size) : null;
              if (Number.isFinite(size) && size > 0) {
                goalMap[b.id] = size;
              }
            });

            this.counterGoalsByBlockId = goalMap;
          } catch (e) {
            // Non-fatal: will fall back to "any activity counts as completed"
            this.counterGoalsByBlockId = {};
          }
        }

        this.habitHeatmaps = buildCounterHeatmaps(
          resp.data || [],
          from,
          to,
          this.currentTimezone,
          this.counterGoalsByBlockId || {}
        );

        const normalised = normalizeTrackingElements(resp.data || [], from, to, this.currentTimezone);
        const elements = normalised.elementsBlock || [];

        this.reportsDateRange = reportsPrepareDates(from, to, reportTimezone);
        this.totalTime = reportsTotalTime(elements);

        this.reportsProjectsBarChart = reportsBarChart(elements, this.reportsDateRange, 'project');
        this.reportsTitlesBarChart = reportsBarChart(elements, this.reportsDateRange, 'title');
        this.reportsColorsBarChart = reportsBarChart(elements, this.reportsDateRange, 'color');

        this.reportsProjectsRangeBarChart = reportsRangeBarChart(elements, 'project');
        this.reportsTitlesRangeBarChart = reportsRangeBarChart(elements, 'title');
        this.reportsColorsRangeBarChart = reportsRangeBarChart(elements, 'color');

        this.reportsProjectsDonutChart = reportsDonutChart(elements, 'project');
        this.reportsTitlesDonutChart = reportsDonutChart(elements, 'title');
        this.reportsColorsDonutChart = reportsDonutChart(elements, 'color');

        this.loadingData = false;
      }
    },
    watch: {
      dateRange: {
        deep: true,
        handler() {
          this.refreshReports();
        }
      }
    },
    mounted() {
      this.refreshReports();
    }
  };
</script>
